// src/crm/ugc/MemberPanel.tsx
//
// Encart « Programme UGC » de la fiche d'un contact `ugc_creator` (spec § 6.5). Contact non
// membre : « Inviter au portail ». Membre : état, adresse de connexion, « Peut facturer »,
// rémunération pour 1 000 vues (RateField, migration 0021), code,
// parrain (modifiable tant que le parrainage n'est pas gagné, l'API refuse sinon), gains dus et
// payés, compte dans l'app et date de fin du Pro offert, « Écrire à … » (son chat avec l'équipe,
// dans l'onglet « Messages »), vidéos ; mettre en pause, réactiver, marquer comme parti, effacer ses données du portail
// (confirmation dans la page, seconde confirmation s'il reste des sommes dues).

import { useEffect, useId, useState } from 'react'
import { Link } from 'react-router-dom'
import { proState, type ProState } from '../../../functions/lib/ugc/pro'
import { formatEuros } from '../../../functions/lib/ugc/rules'
import { ApiClientError } from '../../lib/api'
import type { Creator } from '../../lib/types'
import type { MemberStatus, UgcMember } from '../../lib/ugcTypes'
import { formatDate, MEMBER_STATUS_LABELS, VIDEO_STATUS_LABELS } from './format'
import { useEraseMember, usePatchMember, useUgcMembers, useUgcPayments, useUgcVideos } from './hooks'
import { InviteDialog } from './InviteDialog'
import { RateField } from './RateField'
import './ugc.css'

function Earnings({ member }: { member: UgcMember }) {
  const payments = useUgcPayments()
  if (payments.isPending) return <dd className="ugc-muted">Chargement…</dd>
  if (payments.isError) return <dd className="ugc-muted">Indisponibles</dd>
  // Dû = éléments à régler + factures reçues pas encore payées (même compte que l'effacement).
  const due =
    (payments.data.due.find((g) => g.memberId === member.id)?.totalCents ?? 0) +
    payments.data.open.filter((s) => s.memberId === member.id).reduce((sum, s) => sum + s.totalCents, 0)
  const paid = payments.data.history.filter((s) => s.memberId === member.id).reduce((sum, s) => sum + s.totalCents, 0)
  return (
    <dd>
      Dus : <strong>{formatEuros(due)}</strong>, payés : <strong>{formatEuros(paid)}</strong>
    </dd>
  )
}

function Videos({ member }: { member: UgcMember }) {
  const videos = useUgcVideos('all', { memberId: member.id })
  if (videos.isPending) return <p className="ugc-muted">Chargement des vidéos…</p>
  if (videos.isError) return <p className="ugc-muted">Vidéos indisponibles.</p>
  if (videos.data.length === 0) return <p className="ugc-muted">Aucune vidéo pour l'instant.</p>
  return (
    <ul className="ugc-history-list">
      {videos.data.map((item) => (
        <li key={item.video.id} className="ugc-history-row">
          <span>{item.video.title}</span>
          <span className="ugc-muted">{VIDEO_STATUS_LABELS[item.video.status]}</span>
        </li>
      ))}
    </ul>
  )
}

function ReferrerField({ member }: { member: UgcMember }) {
  const members = useUgcMembers()
  const patch = usePatchMember()
  const id = useId()
  const others = (members.data ?? []).filter((m) => m.id !== member.id)
  return (
    <div className="ugc-field">
      <label htmlFor={id}>Parrain</label>
      <select
        id={id}
        className="ugc-select"
        value={member.referredByMemberId ?? ''}
        disabled={patch.isPending || members.isPending}
        onChange={(e) => patch.mutate({ id: member.id, patch: { referredByMemberId: e.target.value === '' ? null : e.target.value } })}
      >
        <option value="">Aucun</option>
        {others.map((m) => (
          <option key={m.id} value={m.id}>
            {m.displayName} ({m.referralCode})
          </option>
        ))}
      </select>
      {patch.error && (
        <p role="alert" className="ugc-error">
          {patch.error.message}
        </p>
      )}
    </div>
  )
}

function StatusActions({ member }: { member: UgcMember }) {
  const patch = usePatchMember()
  const [leaving, setLeaving] = useState(false)
  const set = (status: MemberStatus) => patch.mutate({ id: member.id, patch: { status } }, { onSuccess: () => setLeaving(false) })

  if (leaving) {
    return (
      <div className="ugc-inline-form">
        <p className="ugc-confirm">{member.displayName} ne pourra plus se connecter au portail.</p>
        <div className="ugc-actions">
          <button type="button" className="ugc-btn ugc-btn--strong" disabled={patch.isPending} onClick={() => set('left')}>
            Confirmer le départ
          </button>
          <button type="button" className="ugc-btn" disabled={patch.isPending} onClick={() => setLeaving(false)}>
            Annuler
          </button>
        </div>
      </div>
    )
  }
  return (
    <>
      <div className="ugc-actions">
        {/* Un invité n'a pas consenti : l'API refuse la pause (R14), seul « parti » lui est possible. */}
        {member.status === 'active' && (
          <button type="button" className="ugc-btn" disabled={patch.isPending} onClick={() => set('paused')}>
            Mettre en pause
          </button>
        )}
        {(member.status === 'paused' || member.status === 'left') && (
          <button type="button" className="ugc-btn" disabled={patch.isPending} onClick={() => set('active')}>
            Réactiver
          </button>
        )}
        {member.status !== 'left' && (
          <button type="button" className="ugc-btn" disabled={patch.isPending} onClick={() => setLeaving(true)}>
            Marquer comme parti
          </button>
        )}
      </div>
      {patch.error && (
        <p role="alert" className="ugc-error">
          {patch.error.message}
        </p>
      )}
    </>
  )
}

function InvoiceToggle({ member }: { member: UgcMember }) {
  const patch = usePatchMember()
  return (
    <dd>
      <label className="ugc-check">
        <input
          type="checkbox"
          checked={member.canInvoice}
          disabled={patch.isPending}
          onChange={(e) => patch.mutate({ id: member.id, patch: { canInvoice: e.target.checked } })}
        />
        <span>{member.canInvoice ? 'Oui' : 'Non'}</span>
      </label>
      {patch.error && (
        <p role="alert" className="ugc-error">
          {patch.error.message}
        </p>
      )}
    </dd>
  )
}

/** Jour de Paris (AAAA-MM-JJ) d'un instant : comparé au dernier jour du Pro. */
function parisToday(now: Date): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Paris' }).format(now)
}

/**
 * Pro offert : l'adresse du compte dans l'app donnée par la personne (celle de sa connexion à
 * l'app, à chercher dans RevenueCat), puis la date de fin du Pro, notée par l'équipe une fois le
 * Pro accordé à la main dans RevenueCat. Aucun appel à RevenueCat ni à Supabase d'ici.
 */
function ProField({ member }: { member: UgcMember }) {
  const patch = usePatchMember()
  const dismiss = usePatchMember()
  const id = useId()
  const [until, setUntil] = useState(member.proUntil ?? '')
  // La date suit le membre : une nouvelle adresse (Pro remis à zéro) ou une date notée ailleurs.
  useEffect(() => {
    setUntil(member.proUntil ?? '')
  }, [member.appAccountEmail, member.proUntil])
  const previous =
    member.previousAppAccountEmail && member.appAccountChangedAt ? (
      <p className="ugc-muted">{`ancienne : ${member.previousAppAccountEmail}, changée le ${formatDate(member.appAccountChangedAt)}`}</p>
    ) : null
  if (member.appAccountEmail === null) {
    return (
      <dd>
        <span className="ugc-muted">Pas encore donnée</span>
        {previous}
      </dd>
    )
  }
  const state = proState(member, parisToday(new Date()))
  return (
    <dd>
      <span>{member.appAccountEmail}</span>
      {previous}
      <p className="ugc-muted">{proAdminText(state, member.proRequestedAt)}</p>
      {state.kind === 'requested' && (
        <div className="ugc-actions">
          <button
            type="button"
            className="ugc-btn"
            disabled={dismiss.isPending}
            onClick={() => dismiss.mutate({ id: member.id, patch: { dismissProRequest: true } })}
          >
            Classer la demande
          </button>
        </div>
      )}
      <div className="ugc-field">
        <label htmlFor={id}>Pro activé jusqu’au</label>
        <input id={id} type="date" className="ugc-input" value={until} onChange={(e) => setUntil(e.target.value)} />
      </div>
      <div className="ugc-actions">
        <button
          type="button"
          className="ugc-btn"
          disabled={patch.isPending || until === (member.proUntil ?? '')}
          onClick={() => patch.mutate({ id: member.id, patch: { proUntil: until === '' ? null : until } })}
        >
          Enregistrer la date du Pro
        </button>
      </div>
      {(patch.error ?? dismiss.error) && (
        <p role="alert" className="ugc-error">
          {(patch.error ?? dismiss.error)?.message}
        </p>
      )}
    </dd>
  )
}

/** État du Pro vu par l'équipe. */
function proAdminText(state: ProState, requestedAt: string | null): string {
  switch (state.kind) {
    case 'active':
      return `Pro actif jusqu'au ${formatDate(state.until)}.`
    case 'requested':
      return `Pro à accorder dans RevenueCat${requestedAt ? `, demandé le ${formatDate(requestedAt)}` : ''}.`
    case 'ended':
      return `Pro terminé le ${formatDate(state.until)}, pas de nouvelle demande.`
    case 'dismissed':
      return 'Demande classée : la personne est invitée à vérifier son adresse.'
    case 'idle':
    case 'missing':
      return 'Pas de demande de Pro.'
  }
}

function EraseAction({ member, onErased }: { member: UgcMember; onErased: () => void }) {
  const erase = useEraseMember()
  const [step, setStep] = useState<'idle' | 'confirm'>('idle')
  const amountsDue = erase.error instanceof ApiClientError && erase.error.code === 'amounts_due'
  // Pro en cours : à retirer dans RevenueCat avant l'effacement (l'adresse disparaît avec le membre).
  const proActive = member.proUntil !== null && member.proUntil >= parisToday(new Date())
  if (step === 'idle') {
    return (
      <div className="ugc-actions">
        <button type="button" className="ugc-btn" onClick={() => setStep('confirm')}>
          Effacer ses données du portail
        </button>
      </div>
    )
  }
  return (
    <div className="ugc-inline-form">
      <p className="ugc-confirm">
        Le compte du portail, les vidéos, les brouillons et les captures de {member.displayName} seront effacés. Les
        règlements déjà faits restent dans l'historique.
      </p>
      {proActive && member.proUntil !== null && (
        <p role="alert" className="ugc-error">
          {`Son Pro court jusqu’au ${formatDate(member.proUntil)} sur ${member.appAccountEmail ?? member.previousAppAccountEmail ?? ''} : retire-le dans RevenueCat avant d’effacer.`}
        </p>
      )}
      {erase.error && (
        <p role="alert" className="ugc-error">
          {erase.error.message}
        </p>
      )}
      <div className="ugc-actions">
        {amountsDue ? (
          <button
            type="button"
            className="ugc-btn ugc-btn--strong"
            disabled={erase.isPending}
            onClick={() => erase.mutate({ id: member.id, confirmDue: true }, { onSuccess: onErased })}
          >
            Effacer malgré les sommes dues
          </button>
        ) : (
          <button type="button" className="ugc-btn ugc-btn--strong" disabled={erase.isPending} onClick={() => erase.mutate({ id: member.id }, { onSuccess: onErased })}>
            Confirmer l'effacement
          </button>
        )}
        <button
          type="button"
          className="ugc-btn"
          disabled={erase.isPending}
          onClick={() => {
            erase.reset()
            setStep('idle')
          }}
        >
          Annuler
        </button>
      </div>
    </div>
  )
}

function MemberDetails({ member, onErased }: { member: UgcMember; onErased: () => void }) {
  return (
    <>
      <dl className="ugc-facts">
        <div>
          <dt>État</dt>
          <dd>{MEMBER_STATUS_LABELS[member.status]}</dd>
        </div>
        <div>
          <dt>E-mail de connexion</dt>
          <dd>{member.email}</dd>
        </div>
        <div>
          <dt>Peut facturer</dt>
          <InvoiceToggle member={member} />
        </div>
        <div>
          <dt>Rémunération pour 1 000 vues</dt>
          <dd>
            <RateField member={member} />
          </dd>
        </div>
        <div>
          <dt>Code de parrainage</dt>
          <dd>{member.referralCode}</dd>
        </div>
        <div>
          <dt>Gains</dt>
          <Earnings member={member} />
        </div>
        <div>
          <dt>Compte dans l'app (Pro)</dt>
          <ProField member={member} />
        </div>
        <div>
          <dt>Dernière connexion</dt>
          <dd>{member.lastLoginAt ? formatDate(member.lastLoginAt) : 'Jamais'}</dd>
        </div>
      </dl>
      {(member.status === 'active' || member.status === 'paused') && (
        <p className="ugc-member-chat">
          <Link className="ugc-link" to={`/crm/ugc/messages?member=${encodeURIComponent(member.id)}`}>
            {`Écrire à ${member.displayName || 'ce membre'}`}
          </Link>
        </p>
      )}
      <ReferrerField member={member} />
      <Videos member={member} />
      <StatusActions member={member} />
      <EraseAction member={member} onErased={onErased} />
    </>
  )
}

export function MemberPanel({ creator }: { creator: Creator }) {
  const members = useUgcMembers(creator.id)
  const [inviting, setInviting] = useState(false)
  // Gardé ici : l'effacement fait disparaître le membre, donc l'encart de détail qui l'a lancé.
  const [erased, setErased] = useState(false)
  const member = members.data?.[0]

  return (
    <section className="ugc-card ugc-member" aria-label="Programme UGC">
      <h2 className="ugc-card-title">Programme UGC</h2>
      {erased && (
        <p role="status" className="ugc-notice">
          Données du portail effacées. Le contact reste dans le CRM.
        </p>
      )}
      {members.isPending && <p className="ugc-muted">Chargement…</p>}
      {members.isError && <p className="ugc-error">Le programme UGC n'a pas pu être chargé : {members.error.message}</p>}
      {members.data && !member && !inviting && (
        <>
          <p className="ugc-muted">Ce contact n'a pas encore accès au portail des créateurs.</p>
          <div className="ugc-actions">
            <button type="button" className="ugc-btn ugc-btn--go" onClick={() => {
                setErased(false)
                setInviting(true)
              }}>
              Inviter au portail
            </button>
          </div>
        </>
      )}
      {members.data && !member && inviting && (
        <InviteDialog creatorId={creator.id} defaultEmail={creator.email ?? ''} onClose={() => setInviting(false)} />
      )}
      {member && <MemberDetails member={member} onErased={() => setErased(true)} />}
    </section>
  )
}
